import React, { useEffect, useState, useRef } from "react";
import { Play, Pause, Check, X, BookOpen, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API, axios, formatClock } from "../../lib/planner";

const TEXTURE = "https://static.prod-images.emergentagent.com/jobs/ba076ba6-cbb6-4319-9066-aa1342f64d2e/images/081040ebe7a7c84b8ab2b0fe8035be59edefdf550c49e2fe2ad159853745d85d.jpeg";

export const FocusMode = ({ entry, onClose, onSaved }) => {
  const [seconds, setSeconds] = useState(0);
  const [running, setRunning] = useState(true);
  const [verse, setVerse] = useState(null);
  const [verseLoading, setVerseLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (running) {
      intervalRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    }
    return () => clearInterval(intervalRef.current);
  }, [running]);

  useEffect(() => {
    const ref = [entry.scripture, entry.chapter].filter(Boolean).join(" ") + (entry.verse ? `:${entry.verse}` : "");
    if (!ref.trim()) return;
    setVerseLoading(true);
    axios
      .get(`${API}/scripture`, { params: { reference: ref.trim() } })
      .then((r) => setVerse(r.data))
      .catch(() => setVerse(null))
      .finally(() => setVerseLoading(false));
  }, [entry]);

  const finish = async () => {
    setSaving(true);
    const minutes = Math.max(1, Math.round(seconds / 60));
    try {
      await axios.put(`${API}/entries/${entry.id}`, {
        started_at: entry.started_at || new Date(Date.now() - seconds * 1000).toISOString(),
        duration_minutes: minutes,
        completed: true,
      });
      toast.success(`${minutes} minute${minutes > 1 ? "s" : ""} of prayer logged. Well done.`);
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error("Could not log this session");
      setSaving(false);
    }
  };

  const scriptureRef = [entry.scripture, entry.chapter, entry.verse].filter(Boolean).join(" ");

  return (
    <div className="fixed inset-0 z-[10000] flex flex-col items-center justify-center overflow-hidden px-6" style={{ backgroundColor: "#1E2620" }} data-testid="focus-mode">
      <div
        className="pointer-events-none absolute inset-0 opacity-30"
        style={{ backgroundImage: `url(${TEXTURE})`, backgroundSize: "cover", backgroundPosition: "center", maskImage: "linear-gradient(to top, rgba(0,0,0,1), transparent 90%)", WebkitMaskImage: "linear-gradient(to top, rgba(0,0,0,1), transparent 90%)" }}
      />
      <button
        onClick={onClose}
        className="absolute right-6 top-6 rounded-full bg-white/10 p-2.5 text-white/80 transition-colors duration-300 hover:bg-white/20"
        data-testid="focus-close-btn"
        aria-label="Exit focus mode"
      >
        <X className="h-5 w-5" />
      </button>

      <div className="relative flex w-full max-w-2xl flex-col items-center text-center">
        <p className="mb-3 font-sans text-xs uppercase tracking-[0.3em] text-[#C4A47C]">In prayer</p>
        <h1 className="font-serif text-4xl leading-tight text-[#F4F1EA] sm:text-5xl" data-testid="focus-prayer-point">
          {entry.prayer_point || "A quiet moment with God"}
        </h1>

        {scriptureRef && (
          <p className="mt-4 flex items-center gap-2 font-sans text-sm uppercase tracking-[0.15em] text-[#9DAe97]">
            <BookOpen className="h-4 w-4" /> {scriptureRef}
          </p>
        )}
        {verseLoading && <p className="mt-4 font-serif text-lg italic text-white/50">Opening the Word…</p>}
        {verse && (
          <p className="mt-4 max-w-xl whitespace-pre-line font-serif text-xl italic leading-relaxed text-[#E8E4D8]" data-testid="focus-verse">
            “{verse.text}”
          </p>
        )}

        <div className="my-10 font-serif text-7xl tabular-nums tracking-tight text-white sm:text-8xl" data-testid="focus-timer">
          {formatClock(seconds)}
        </div>

        <div className="flex items-center gap-4">
          <button
            onClick={() => setRunning((r) => !r)}
            className="flex h-14 w-14 items-center justify-center rounded-full border border-white/25 text-white transition-colors duration-300 hover:bg-white/10"
            data-testid="focus-toggle-btn"
            aria-label={running ? "Pause" : "Resume"}
          >
            {running ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6" />}
          </button>
          <button
            onClick={finish}
            disabled={saving}
            className="flex items-center gap-2 rounded-full bg-[#6B8E63] px-7 py-4 font-sans text-base font-medium text-white transition-colors duration-300 hover:bg-[#5c7d55] disabled:opacity-60"
            data-testid="focus-finish-btn"
          >
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Check className="h-5 w-5" />}
            Finish & log prayer
          </button>
        </div>
        <p className="mt-6 font-sans text-sm text-white/40">Your time in prayer will be logged toward this week.</p>
      </div>
    </div>
  );
};
